import { useEffect, useState } from 'react';
import type { Catalogs } from '../types/api';
import { useApi } from './client';

// Module-level cache. Catalogs are tiny, change rarely, and are safe to share
// for the lifetime of the SPA (one user per session). Cleared by a hard refresh.
let cache: Catalogs | null = null;
let pending: Promise<Catalogs> | null = null;

interface UseCatalogsResult {
  catalogs: Catalogs | null;
  error: string | null;
  reload: () => void;
}

export function useCatalogs(): UseCatalogsResult {
  const apiFetch = useApi();
  const [catalogs, setCatalogs] = useState<Catalogs | null>(cache);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (cache && tick === 0) {
      setCatalogs(cache);
      return;
    }
    if (!pending) {
      pending = apiFetch('/catalogs')
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<Catalogs>;
        })
        .then((data) => {
          cache = data;
          return data;
        })
        .finally(() => {
          // Drop the in-flight ref once settled so reload() can re-fire.
          pending = null;
        });
    }
    pending
      .then((data) => {
        if (!cancelled) setCatalogs(data);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch, tick]);

  return {
    catalogs,
    error,
    reload: () => {
      cache = null;
      setTick((t) => t + 1);
    },
  };
}